import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../utils/api';

// --- THEME DEFINITIONS ---

export type ThemeId = string; // IDs are now strings from DB

export interface GameTheme {
    _id: string; // MongoDB ID
    id?: string; // Legacy ID support if needed
    name: string; // Changed from label to name to match DB
    cssVariables: Record<string, string>; // Changed from fixed object to Record
    isDefault?: boolean;
    config?: {
        particles: 'DUST' | 'DIGITAL_RAIN' | 'NONE';
        vignetteOpacity: number;
        overlayTexture: string;
    };
}

// Fallback theme in case DB is empty or fails
const FALLBACK_THEME: GameTheme = {
    _id: 'fallback',
    name: 'Fallback Sepia',
    cssVariables: {
        '--bg-primary': '#1c1917',
        '--bg-secondary': '#292524',
        '--text-primary': '#e7e5e4',
        '--text-secondary': '#a8a29e',
        '--accent-glow': '#d4af37',
        '--accent-color': '#f59e0b',
        '--border-color': 'rgba(212, 175, 55, 0.3)',
        '--glass-bg': 'rgba(28, 25, 23, 0.7)',
        '--glass-border': 'rgba(212, 175, 55, 0.15)',
    },
    isDefault: true
};

// --- CONTEXT ---

interface ThemeContextType {
    currentTheme: GameTheme;
    setTheme: (id: string) => void;
    availableThemes: GameTheme[];
    isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentTheme, setCurrentTheme] = useState<GameTheme>(FALLBACK_THEME);
    const [availableThemes, setAvailableThemes] = useState<GameTheme[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Fetch themes from DB on mount
    useEffect(() => {
        const fetchThemes = async () => {
            try {
                const { data } = await api.get('/themes');
                if (Array.isArray(data) && data.length > 0) {
                    setAvailableThemes(data);

                    // Find default theme from DB
                    const defaultTheme = data.find((t: GameTheme) => t.isDefault);
                    if (defaultTheme) {
                        setCurrentTheme(defaultTheme);
                    } else {
                        // If no default marked, take the first one
                        setCurrentTheme(data[0]);
                    }
                }
            } catch (error) {
                console.error('[ThemeSystem] Failed to fetch themes, using fallback', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchThemes();
    }, []);

    // Apply CSS variables whenever currentTheme changes
    useEffect(() => {
        if (!currentTheme) return;

        const root = document.documentElement;
        // Apply all CSS variables from the theme
        Object.entries(currentTheme.cssVariables).forEach(([key, value]) => {
            root.style.setProperty(key, value);
        });

        console.log(`[ThemeSystem] Applied theme: ${currentTheme.name}`);
    }, [currentTheme]);

    const handleSetTheme = (themeId: string) => {
        const theme = availableThemes.find(t => t._id === themeId);
        if (theme) {
            setCurrentTheme(theme);
        }
    };

    const value = {
        currentTheme,
        setTheme: handleSetTheme,
        availableThemes,
        isLoading
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
